/**
 * Authentication utility functions
 * Handles token validation, expiration, and authentication state management
 */

/**
 * Validates if the current token is still valid
 * @returns {boolean} - true if token is valid, false otherwise
 */
export const validateToken = () => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      console.log('No token found in localStorage');
      return false;
    }

    // Check if token is expired by decoding the JWT payload
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      
      if (payload.exp && payload.exp < currentTime) {
        console.log('Token has expired');
        return false;
      }
      
      console.log('Token is valid');
      return true;
    } catch (decodeError) {
      console.error('Error decoding token:', decodeError);
      return false;
    }
  } catch (error) {
    console.error('Error validating token:', error);
    return false;
  }
};

/**
 * Gets a valid token from localStorage
 * @returns {string|null} - valid token or null if not found/expired
 */
export const getValidToken = () => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    console.log('No token found in localStorage');
    return null;
  }

  // Validate token before returning
  if (!validateToken()) {
    console.log('Token is invalid or expired');
    return null;
  }

  return token;
};

/**
 * Handles token expiration by clearing authentication data
 * and redirecting to login page
 */
export const handleTokenExpiration = () => {
  console.log('Handling token expiration...');
  
  // Clear authentication data
  localStorage.removeItem('token');
  localStorage.removeItem('role');
  localStorage.removeItem('user');
  
  // Clear any other authentication-related data
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('tokenExpiry');
  
  // Redirect to login page
  window.location.href = '/login';
};

/**
 * Checks if user is authenticated (has valid token)
 * @returns {boolean} - true if authenticated, false otherwise
 */
export const isAuthenticated = () => {
  return validateToken();
};

/**
 * Gets user role from localStorage
 * @returns {string|null} - user role or null if not found
 */
export const getUserRole = () => {
  return localStorage.getItem('role');
};

/**
 * Checks if user has specific role
 * @param {string} role - role to check for
 * @returns {boolean} - true if user has the role, false otherwise
 */
export const hasRole = (role) => {
  const userRole = getUserRole();
  return userRole === role;
};

/**
 * Logs out user by clearing all authentication data
 */
export const logout = () => {
  console.log('Logging out user...');
  
  // Clear all authentication data
  localStorage.removeItem('token');
  localStorage.removeItem('role');
  localStorage.removeItem('user');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('tokenExpiry');
  
  // Redirect to login page
  window.location.href = '/login';
};

/**
 * Sets authentication data in localStorage
 * @param {string} token - JWT token
 * @param {string} role - user role
 * @param {object} user - user data (optional)
 */
export const setAuthData = (token, role, user = null) => {
  localStorage.setItem('token', token);
  localStorage.setItem('role', role);
  
  if (user) {
    localStorage.setItem('user', JSON.stringify(user));
  }
  
  console.log('Authentication data set successfully');
};

/**
 * Gets token expiration time
 * @returns {number|null} - expiration timestamp or null if token is invalid
 */
export const getTokenExpiration = () => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      return null;
    }

    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null; // Convert to milliseconds
  } catch (error) {
    console.error('Error getting token expiration:', error);
    return null;
  }
};

/**
 * Checks if token will expire soon (within specified minutes)
 * @param {number} minutes - minutes before expiration to check
 * @returns {boolean} - true if token expires soon, false otherwise
 */
export const isTokenExpiringSoon = (minutes = 5) => {
  const expiration = getTokenExpiration();
  
  if (!expiration) {
    return false;
  }
  
  const currentTime = Date.now();
  const timeUntilExpiration = expiration - currentTime;
  const minutesUntilExpiration = timeUntilExpiration / (1000 * 60);
  
  return minutesUntilExpiration <= minutes;
};

/**
 * Refreshes token if it's expiring soon
 * This is a placeholder for future token refresh implementation
 * @returns {Promise<boolean>} - true if token was refreshed, false otherwise
 */
export const refreshTokenIfNeeded = async () => {
  if (isTokenExpiringSoon(10)) { // Refresh if expiring within 10 minutes
    console.log('Token is expiring soon, attempting refresh...');
    
    // TODO: Implement actual token refresh logic
    // For now, just return false to indicate no refresh was performed
    return false;
  }
  
  return false;
};
